import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Palette, Pencil, PenTool, Sparkles, Brush, Layers,
  Feather, TestTube, Fingerprint, ArrowLeft, ShieldCheck,
} from "lucide-react";

/* ───────────────────────────────────────────────────────
   THE COLORING EXPERIENCE GUIDE — World of INSPIYAH
   A styled walkthrough for the Book of Colorz coloring journey.
   Signed with the CreatorEcho mark + creation-provenance note.
   ─────────────────────────────────────────────────────── */

const GUARDIANS_BG =
  "https://customer-assets-jt897jd0.emergentagent.net/job_13a69085-3e8c-47da-9b87-61277eb4e9f9/artifacts/452r80h5_Guardians%20-%20ART%20Energi%20Triad%20of%20Excellence%201.png";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } },
};
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.12 } } };

function Reveal({ children, className = "", variants = fadeUp }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div ref={ref} variants={variants} initial="hidden" animate={inView ? "visible" : "hidden"} className={className}>
      {children}
    </motion.div>
  );
}

/* CreatorEcho sigil — an interlocked "C·E" inside concentric echo-rings + a rising seed. */
const EchoSigil = ({ size = 64, glow = true, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 100 100" fill="none" className={className}
       style={glow ? { filter: "drop-shadow(0 0 6px rgba(212,162,84,0.45))" } : undefined}>
    <circle cx="50" cy="50" r="46" stroke="rgba(212,162,84,0.35)" strokeWidth="1" />
    <circle cx="50" cy="50" r="38" stroke="rgba(212,162,84,0.6)" strokeWidth="1.4" />
    <circle cx="50" cy="50" r="30" stroke="rgba(232,200,122,0.35)" strokeWidth="0.8" strokeDasharray="2 4" />
    {/* echo arcs (the "E" as resonance) */}
    <path d="M62 30 A26 26 0 1 0 62 70" stroke="#e8c87a" strokeWidth="3.2" strokeLinecap="round" />
    <path d="M56 40 A16 16 0 1 0 56 60" stroke="#d4a254" strokeWidth="2.4" strokeLinecap="round" />
    {/* rising seed / flame — the "creation" spark */}
    <path d="M50 60 C46 52 46 46 50 40 C54 46 54 52 50 60 Z" fill="#e8c87a" />
    <circle cx="50" cy="63" r="2.2" fill="#f5d78e" />
  </svg>
);

const StarRow = ({ filled, label, tone }) => (
  <div className="flex items-center gap-2">
    <span className="tracking-widest" style={{ color: tone, fontSize: "0.95rem", letterSpacing: "2px" }}>
      {"★".repeat(filled)}
      <span style={{ color: "rgba(201,184,150,0.25)" }}>{"★".repeat(5 - filled)}</span>
    </span>
    <span className="colox-sans" style={{ color: tone, fontSize: "0.72rem", fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase" }}>
      {label}
    </span>
  </div>
);

const TOOLKIT = [
  {
    icon: Palette, name: "Crayons", stars: 5, rating: "This Edition's Heart", tone: "#8fd694", featured: true,
    blurb: "Crayon is the truest companion for these Seedz. On this paper it glides, catches the natural grain, and lets color settle in like light finding its way home.",
    tries: ["Light pressure for soft, transparent color", "Medium pressure for richer, fuller coverage", "Layering colorz to discover new ones", "Cross-hatching to invite texture", "Blending directly on the page"],
    tip: "When you want more color, don't press harder — lay another gentle layer. The page rewards patience.",
  },
  {
    icon: Pencil, name: "Colored Pencils", stars: 5, rating: "This Edition's Heart", tone: "#8fd694", featured: true,
    blurb: "Colored pencil was made for this surface — a tool for the slow, intentional work of detail, shading, and quiet transitions.",
    tries: ["Light-to-dark layering", "Many colorz woven into one area", "Gradual shifts in pressure", "Burnishing for smooth, glowing areas", "Cross-hatching for depth"],
    tip: "Start lighter than you think you need. Intensity is always waiting to be built.",
  },
  {
    icon: PenTool, name: "Fine-Tip & Ballpoint Pens", stars: 3, rating: "Tested — One Type", tone: "#e8c87a",
    blurb: "Pens can bless the details — outlines, accents, small sacred marks. In our own testing we've explored one type of color pen on this uncoated stock; because this surface is crayon-and-pencil-first, some inks may drink into the page.",
    tries: ["Fine detail & linework", "Clean outlines", "Small accents"],
    tip: "Test your own pen on the Test Page before committing it to a finished piece — every ink meets this paper in its own way.",
  },
  {
    icon: Sparkles, name: "Gel Pens", stars: 3, rating: "Experiment", tone: "#e8c87a",
    blurb: "Gel pens can add shimmer, lettering, and small pools of vivid color. Different inks behave differently on an uncoated page.",
    tries: ["Highlights & shimmer", "Hand-lettering", "Small vibrant fills"],
    tip: "Let the ink dry fully before closing or stacking pages.",
  },
  {
    icon: Brush, name: "Markers", stars: 2, rating: "Use With Care", tone: "#e0a06a",
    blurb: "Markers can lay down bold, beautiful color — but wet mediums may show through or bleed on this paper depending on the ink.",
    tries: ["Bold statement color", "Backgrounds (with a gentle hand)"],
    tip: "Always test first. For heavier markers, slip a protective sheet behind the page.",
  },
];

const TESTS = [
  { tool: "Crayon", steps: "Color lightly → Color heavily → Layer two colors" },
  { tool: "Colored Pencil", steps: "Light pressure → Layer → Blend" },
  { tool: "Pen", steps: "Fine line → Detail → Pressure test" },
  { tool: "Gel Pen", steps: "Line → Fill → Dry-time test" },
  { tool: "Marker", steps: "Light coverage → Heavy coverage → Show-through test" },
];

const JOURNEY = [
  { icon: Feather, title: "Explore", body: "Don't worry about finishing the page. Experiment with color." },
  { icon: Layers, title: "Build", body: "Start light and gradually build intensity." },
  { icon: Palette, title: "Layer", body: "Combine colors rather than searching for the perfect single color." },
  { icon: Brush, title: "Texture", body: "Let the paper and your tool create part of the artwork." },
  { icon: Fingerprint, title: "Personalize", body: "There is no required palette. Your interpretation is the finished piece." },
];

const AUDIENCE = [
  { tier: "Children / Family", audience: "Children & Family Coloring Book", purpose: "Discover, explore, and begin forming connections", journey: "Discover · Explain · Explore", journeyDesc: "Parent-guided family exploration — educational & creative", guardian: "Strongly encouraged", tone: "#8fd694" },
  { tier: "Young-Adult / Teen", audience: "Young-Adult & Teen Art", purpose: "Question, interpret, and explore meaning", journey: "Question · Interpret · Connect", journeyDesc: "Artistic & spiritual exploration", guardian: "Encouraged, for added conversation", tone: "#e8c87a" },
  { tier: "General-Adult", audience: "General-Adult Collectible Art Book", purpose: "Contemplate, examine, and experience the full artistic & philosophical work", journey: "Reflect · Contemplate · Examine · Integrate", journeyDesc: "Art, philosophy, spirituality & symbolism", guardian: "—", tone: "#d4a254" },
];

const ColoringExperienceGuide = () => {
  return (
    <div className="relative min-h-screen" data-testid="coloring-guide-page"
         style={{ background: "#0a0a0f", fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,300;1,400&family=Raleway:wght@300;400;500;600&display=swap');
        .colox-serif { font-family: 'Cormorant Garamond', Georgia, serif; }
        .colox-sans { font-family: 'Raleway', 'Segoe UI', sans-serif; }
        .cg-card { background: linear-gradient(160deg, rgba(26,21,37,0.82), rgba(15,12,20,0.92)); border: 1px solid rgba(212,162,84,0.14); transition: transform 0.4s ease, border-color 0.4s ease, box-shadow 0.4s ease; }
        .cg-card:hover { transform: translateY(-4px); border-color: rgba(212,162,84,0.42); box-shadow: 0 18px 44px rgba(0,0,0,0.5), 0 0 30px rgba(212,162,84,0.12); }
        .cg-divider { height: 1px; background: linear-gradient(90deg, transparent, rgba(212,162,84,0.45), transparent); }
        .cg-glow { text-shadow: 0 0 26px rgba(232,200,122,0.28); }
        .cg-watermark { background-image: radial-gradient(circle at 1px 1px, rgba(212,162,84,0.05) 1px, transparent 0); background-size: 26px 26px; }
      `}</style>

      {/* Sacred guardian backdrop */}
      <div className="fixed inset-0 pointer-events-none" style={{
        backgroundImage: `url(${GUARDIANS_BG})`, backgroundSize: "cover", backgroundPosition: "center top",
        opacity: 0.14, mixBlendMode: "screen",
      }} />
      <div className="fixed inset-0 pointer-events-none cg-watermark" />
      <div className="fixed inset-0 pointer-events-none" style={{
        background: "linear-gradient(to bottom, rgba(10,10,15,0.72), rgba(10,10,15,0.82))",
      }} />

      <div className="relative z-10">
        {/* Ribbon */}
        <div className="w-full text-center py-2 px-4"
             style={{ background: "linear-gradient(90deg, #1a1525, #1e1830, #1a1525)", borderBottom: "1px solid rgba(212,162,84,0.15)" }}>
          <span className="colox-sans" style={{ color: "#e8c87a", fontSize: "0.7rem", letterSpacing: "0.22em", textTransform: "uppercase" }}>
            Where Colorz Unite Us! · Color Your Way
          </span>
        </div>

        {/* Hero */}
        <section className="px-6 pt-16 pb-12 text-center max-w-4xl mx-auto">
          <Reveal>
            <Link to="/book-of-colorz" data-testid="guide-back-link"
                  className="colox-sans inline-flex items-center gap-2 mb-8 hover:opacity-80 transition-opacity"
                  style={{ color: "rgba(201,184,150,0.7)", fontSize: "0.72rem", letterSpacing: "0.18em", textTransform: "uppercase" }}>
              <ArrowLeft size={14} /> Back to the Book of Colorz
            </Link>
            <p className="colox-sans mb-5" style={{ color: "#d4a254", fontSize: "0.72rem", letterSpacing: "0.32em", textTransform: "uppercase" }}>
              World of INSPIYAH · Book of Colorz
            </p>
            <div className="flex justify-center mb-6"><EchoSigil size={72} /></div>
            <h1 className="colox-serif cg-glow font-light leading-tight mb-5"
                style={{ color: "#f5efe6", fontSize: "clamp(2.6rem, 6vw, 4.5rem)" }} data-testid="guide-title">
              The Coloring Experience Guide
            </h1>
            <p className="colox-serif italic font-light" style={{ color: "#c9b896", fontSize: "clamp(1.1rem,2.4vw,1.5rem)" }}>
              How to enter the pages with intention — and let color become a quiet conversation with the One who made you.
            </p>
          </Reveal>
        </section>

        {/* Start With the Paper */}
        <section className="px-6 pb-16 max-w-3xl mx-auto">
          <Reveal className="cg-card rounded-2xl p-8 sm:p-10">
            <div className="flex items-center gap-3 mb-4">
              <Feather size={22} style={{ color: "#d4a254" }} />
              <h2 className="colox-serif font-light" style={{ color: "#f5efe6", fontSize: "1.9rem" }}>Start With the Paper</h2>
            </div>
            <div className="cg-divider mb-6" />
            <p className="colox-serif mb-4" style={{ color: "#c9b896", fontSize: "1.12rem", lineHeight: 1.75 }}>
              Before a single color touches the page, know this: the paper itself was chosen with intention. These Seedz are
              printed on a natural, <strong style={{ color: "#e8c87a" }}>uncoated stock</strong> — a surface with grain and breath,
              so crayon and colored pencil can build, layer, blend, and settle into the page the way a seed settles into soil.
            </p>
            <p className="colox-serif mb-6" style={{ color: "#c9b896", fontSize: "1.12rem", lineHeight: 1.75 }}>
              This is not a slick, ink-first surface. It was made for the hand that loves to slow down — for the tactile,
              meditative act of coloring itself. So don't rush. Feel the tool move across the page. Let each mark be yours.
            </p>
            <div className="flex flex-wrap gap-2">
              {["Try light pressure", "Build your color", "Layer", "Blend", "Experiment"].map((t) => (
                <span key={t} className="colox-sans rounded-full px-4 py-1.5"
                      style={{ color: "#e8c87a", background: "rgba(212,162,84,0.08)", border: "1px solid rgba(212,162,84,0.22)", fontSize: "0.78rem", letterSpacing: "0.04em" }}>
                  {t}
                </span>
              ))}
            </div>
            <p className="colox-serif italic mt-6" style={{ color: "rgba(212,162,84,0.75)", fontSize: "1.05rem" }}>
              There is no single right way to color. There is only your way.
            </p>
            <div className="rounded-xl px-5 py-4 mt-6" style={{ background: "rgba(212,162,84,0.06)", border: "1px solid rgba(212,162,84,0.2)" }}>
              <p className="colox-sans mb-1" style={{ color: "#d4a254", fontSize: "0.66rem", letterSpacing: "0.18em", textTransform: "uppercase" }}>A Note on Editions</p>
              <p className="colox-serif" style={{ color: "rgba(201,184,150,0.88)", fontSize: "1.02rem", lineHeight: 1.7 }}>
                This first INSPIYAH edition is tuned for <strong style={{ color: "#e8c87a" }}>crayon and colored pencil</strong> — the most
                natural way to experience these Seedz. For the artist who colors mostly with pens, a heavier-weight edition is on
                the horizon, crafted with pen work in mind. Same Seedz — a different surface for a different hand.
              </p>
            </div>
          </Reveal>
        </section>

        {/* Your Coloring Toolkit */}
        <section className="px-6 pb-16 max-w-5xl mx-auto">
          <Reveal className="text-center mb-10">
            <h2 className="colox-serif font-light mb-3" style={{ color: "#f5efe6", fontSize: "2.4rem" }}>Your Coloring Toolkit</h2>
            <p className="colox-serif italic" style={{ color: "rgba(201,184,150,0.75)", fontSize: "1.05rem" }}>
              Every tool meets the page differently. For this edition, crayon and colored pencil lead the way.
            </p>
          </Reveal>
          <Reveal variants={stagger} className="grid gap-6 md:grid-cols-2">
            {TOOLKIT.map((tool) => {
              const Icon = tool.icon;
              return (
                <motion.div key={tool.name} variants={fadeUp} className="cg-card rounded-2xl p-7"
                            style={tool.featured ? { borderColor: "rgba(212,162,84,0.45)", boxShadow: "0 0 30px rgba(212,162,84,0.1)" } : undefined}
                            data-testid={`toolkit-card-${tool.name.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}>
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
                           style={{ background: "linear-gradient(135deg, rgba(212,162,84,0.18), rgba(232,200,122,0.08))", border: "1px solid rgba(212,162,84,0.3)" }}>
                        <Icon size={20} style={{ color: "#e8c87a" }} />
                      </div>
                      <h3 className="colox-serif font-semibold" style={{ color: "#f5efe6", fontSize: "1.35rem" }}>{tool.name}</h3>
                    </div>
                    {tool.featured && (
                      <span className="colox-sans rounded-full px-3 py-1 flex-shrink-0" data-testid="toolkit-featured-badge"
                            style={{ background: "linear-gradient(135deg, #d4a254, #e8c87a)", color: "#0a0a0f", fontSize: "0.58rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", whiteSpace: "nowrap" }}>
                        ★ For This Edition
                      </span>
                    )}
                  </div>
                  <div className="mb-4"><StarRow filled={tool.stars} label={tool.rating} tone={tool.tone} /></div>
                  <p className="colox-serif mb-4" style={{ color: "#c9b896", fontSize: "1.02rem", lineHeight: 1.65 }}>{tool.blurb}</p>
                  <ul className="space-y-1.5 mb-5">
                    {tool.tries.map((t) => (
                      <li key={t} className="colox-sans flex items-start gap-2" style={{ color: "rgba(201,184,150,0.85)", fontSize: "0.85rem" }}>
                        <span style={{ color: "#d4a254" }}>·</span>{t}
                      </li>
                    ))}
                  </ul>
                  <div className="rounded-lg px-4 py-3" style={{ background: "rgba(212,162,84,0.06)", borderLeft: "2px solid rgba(212,162,84,0.5)" }}>
                    <p className="colox-sans" style={{ color: "#e8c87a", fontSize: "0.8rem", lineHeight: 1.55 }}>
                      <strong>Tip:</strong> {tool.tip}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </Reveal>
        </section>

        {/* The Test Page */}
        <section className="px-6 pb-16 max-w-3xl mx-auto">
          <Reveal className="cg-card rounded-2xl p-8 sm:p-10">
            <div className="flex items-center gap-3 mb-2">
              <TestTube size={22} style={{ color: "#d4a254" }} />
              <h2 className="colox-serif font-light" style={{ color: "#f5efe6", fontSize: "1.9rem" }}>The Test Page — Before You Begin</h2>
            </div>
            <p className="colox-serif italic mb-6" style={{ color: "rgba(201,184,150,0.72)", fontSize: "1.02rem" }}>
              Your coloring tools are part of your artistic signature. Discover how each one meets this paper before you begin.
            </p>
            <div className="rounded-xl px-5 py-4 mb-6" style={{ background: "rgba(212,162,84,0.06)", border: "1px solid rgba(212,162,84,0.2)" }}>
              <p className="colox-sans mb-1" style={{ color: "#d4a254", fontSize: "0.66rem", letterSpacing: "0.18em", textTransform: "uppercase" }}>Your Test Page · Page 6</p>
              <p className="colox-serif" style={{ color: "rgba(201,184,150,0.9)", fontSize: "1.02rem", lineHeight: 1.7 }}>
                Turn to <strong style={{ color: "#e8c87a" }}>Page 6</strong> — the Yahffirmation of <strong style={{ color: "#e8c87a" }}>Psalm 83:18</strong>.
                Two triangles rise to hold the scripture between them, and an Egyptian-style winged disk spreads its wingspan beneath the words.
                Let these bold shapes be your proving ground — test each tool within the triangles and along the wingspan before you commit it to a Seed you love.
              </p>
            </div>
            <div className="cg-divider mb-6" />
            <div className="space-y-4">
              {TESTS.map((row) => (
                <div key={row.tool} className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 pb-3"
                     style={{ borderBottom: "1px solid rgba(212,162,84,0.1)" }}>
                  <span className="colox-sans" style={{ color: "#e8c87a", fontSize: "0.82rem", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", minWidth: "130px" }}>
                    {row.tool}
                  </span>
                  <span className="colox-serif" style={{ color: "#c9b896", fontSize: "1.05rem" }}>{row.steps}</span>
                </div>
              ))}
            </div>
            <p className="colox-serif italic mt-6" style={{ color: "rgba(212,162,84,0.7)", fontSize: "1.02rem" }}>
              What works beautifully for one artist may behave differently for another. That's why we encourage you to test first.
            </p>
          </Reveal>
        </section>

        {/* Your Coloring Experience */}
        <section className="px-6 pb-16 max-w-5xl mx-auto">
          <Reveal className="text-center mb-10">
            <h2 className="colox-serif font-light" style={{ color: "#f5efe6", fontSize: "2.4rem" }}>Your Coloring Experience</h2>
          </Reveal>
          <Reveal variants={stagger} className="grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
            {JOURNEY.map((step, i) => {
              const Icon = step.icon;
              return (
                <motion.div key={step.title} variants={fadeUp} className="cg-card rounded-2xl p-6 text-center"
                            data-testid={`journey-step-${step.title.toLowerCase()}`}>
                  <div className="colox-serif mx-auto mb-4 w-11 h-11 rounded-full flex items-center justify-center font-semibold"
                       style={{ background: "linear-gradient(135deg, #d4a254, #e8c87a)", color: "#0a0a0f", fontSize: "1.05rem" }}>
                    {i + 1}
                  </div>
                  <Icon size={22} className="mx-auto mb-3" style={{ color: "#d4a254" }} />
                  <h3 className="colox-serif font-semibold mb-2" style={{ color: "#e8c87a", fontSize: "1.25rem" }}>{step.title}</h3>
                  <p className="colox-sans" style={{ color: "rgba(201,184,150,0.85)", fontSize: "0.82rem", lineHeight: 1.6 }}>{step.body}</p>
                </motion.div>
              );
            })}
          </Reveal>
        </section>

        {/* Who This Journey Is For — audience matrix + representation ethic */}
        <section className="px-6 pb-16 max-w-5xl mx-auto">
          <Reveal className="text-center mb-8">
            <h2 className="colox-serif font-light mb-4" style={{ color: "#f5efe6", fontSize: "2.4rem" }}>Who This Journey Is For</h2>
            <p className="colox-serif italic max-w-2xl mx-auto" style={{ color: "rgba(201,184,150,0.82)", fontSize: "1.05rem", lineHeight: 1.7 }}>
              Coloring for YAH — <span style={{ color: "#e8c87a" }}>Health · Wealth · Happiness</span>. The Universal Pillars of Success, where ancient principles are bridged with spiritual concepts and illustrations — Yahffirmations to the Alpha &amp; Omega, Universal Creator.
            </p>
          </Reveal>
          <Reveal variants={stagger} className="grid gap-6 md:grid-cols-3">
            {AUDIENCE.map((a) => (
              <motion.div key={a.tier} variants={fadeUp} className="cg-card rounded-2xl p-7 flex flex-col"
                          data-testid={`audience-${a.tier.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}>
                <span className="colox-sans rounded-full px-3 py-1 self-start mb-4"
                      style={{ color: a.tone, background: "rgba(212,162,84,0.08)", border: `1px solid ${a.tone}55`, fontSize: "0.62rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase" }}>
                  {a.tier}
                </span>
                <h3 className="colox-serif font-semibold mb-4" style={{ color: "#f5efe6", fontSize: "1.3rem", lineHeight: 1.2 }}>{a.audience}</h3>
                <p className="colox-sans mb-1" style={{ color: "rgba(201,184,150,0.55)", fontSize: "0.6rem", letterSpacing: "0.12em", textTransform: "uppercase" }}>Primary Purpose</p>
                <p className="colox-serif mb-4" style={{ color: "#c9b896", fontSize: "1rem", lineHeight: 1.55 }}>{a.purpose}</p>
                <p className="colox-sans mb-1" style={{ color: "rgba(201,184,150,0.55)", fontSize: "0.6rem", letterSpacing: "0.12em", textTransform: "uppercase" }}>The Coloring Journey</p>
                <p className="colox-serif italic mb-1" style={{ color: a.tone, fontSize: "1.02rem" }}>{a.journey}</p>
                <p className="colox-sans mb-4" style={{ color: "rgba(201,184,150,0.78)", fontSize: "0.82rem", lineHeight: 1.5 }}>{a.journeyDesc}</p>
                <div className="mt-auto pt-4" style={{ borderTop: "1px solid rgba(212,162,84,0.12)" }}>
                  <p className="colox-sans mb-1" style={{ color: "rgba(201,184,150,0.55)", fontSize: "0.6rem", letterSpacing: "0.12em", textTransform: "uppercase" }}>Parent / Guardian Role</p>
                  <p className="colox-serif" style={{ color: "#e8c87a", fontSize: "0.98rem" }}>{a.guardian}</p>
                </div>
              </motion.div>
            ))}
          </Reveal>

          <Reveal className="cg-card rounded-2xl p-8 sm:p-10 mt-8" data-testid="representation-note">
            <div className="flex items-center gap-3 mb-3">
              <ShieldCheck size={20} style={{ color: "#d4a254" }} />
              <h3 className="colox-serif font-light" style={{ color: "#f5efe6", fontSize: "1.6rem" }}>On the Human Form &amp; Cultural Representation</h3>
            </div>
            <div className="cg-divider mb-5" />
            <p className="colox-serif mb-4" style={{ color: "#c9b896", fontSize: "1.08rem", lineHeight: 1.75 }}>
              INSPIYAH may incorporate artistic representations of the human form as expressions of nature, creation, ancestry, spirituality, identity, and human experience.
            </p>
            <p className="colox-serif mb-4" style={{ color: "#c9b896", fontSize: "1.08rem", lineHeight: 1.75 }}>
              Such representations are evaluated according to their artistic context, composition, purpose, and intended audience rather than the presence of individual anatomical features alone.
            </p>
            <p className="colox-serif" style={{ color: "#c9b896", fontSize: "1.08rem", lineHeight: 1.75 }}>
              Cultural and ancestral references should be presented respectfully and should not be used to sexualize, stereotype, or sensationalize the people or traditions represented.
            </p>
            <p className="colox-sans mt-5" style={{ color: "rgba(201,184,150,0.72)", fontSize: "0.85rem", lineHeight: 1.6 }}>
              Prefer to preview first? The free <Link to="/book-of-colorz#seedz" data-testid="seedz-preview-link" style={{ color: "#e8c87a", textDecoration: "underline" }}>Seedz download</Link> offers a representative example on <strong style={{ color: "#e8c87a" }}>page 9</strong>.
            </p>
          </Reveal>
        </section>

        {/* A Note From the Creator */}
        <section className="px-6 pb-16 max-w-3xl mx-auto">
          <Reveal className="text-center">
            <div className="flex justify-center mb-6"><EchoSigil size={56} /></div>
            <h2 className="colox-serif font-light italic mb-6" style={{ color: "#f5efe6", fontSize: "2rem" }}>A Note From the Creator</h2>
            <p className="colox-serif mb-4" style={{ color: "#c9b896", fontSize: "1.18rem", lineHeight: 1.8 }}>
              These pages were never simply something to fill. They were planted as a place to slow down, to breathe, to create —
              a quiet room within your faith where color becomes a kind of meditation and mental stimulation to INSPIYAH.
            </p>
            <p className="colox-serif mb-6" style={{ color: "#c9b896", fontSize: "1.18rem", lineHeight: 1.8 }}>
              The paper, the artwork, the tools — and above all, your creative hand — all become part of the offering.
            </p>
            <p className="colox-serif italic cg-glow" style={{ color: "#e8c87a", fontSize: "1.5rem" }}>
              So choose your colorz. Take your time. Let the page become yours.
            </p>
            <p className="colox-serif tracking-widest mt-6" style={{ color: "#d4a254", fontSize: "1.05rem", letterSpacing: "0.3em", textTransform: "uppercase" }}>
              Where Colorz Unite Us · Color Your Way
            </p>
          </Reveal>
        </section>

        {/* CreatorEcho signature + provenance */}
        <section className="px-6 pb-24 max-w-3xl mx-auto">
          <div className="cg-divider mb-10" />
          <Reveal className="cg-card rounded-2xl p-8 sm:p-10 text-center" >
            <div className="flex items-center justify-center mb-5">
              <img
                src="/assets/creatorecho-seal.png"
                alt="CreatorEcho Seal — Created by Keeper of the Fiyah, A CreatorEcho Work"
                data-testid="creatorecho-seal"
                style={{ width: 168, height: 168, objectFit: "cover", borderRadius: "50%", filter: "drop-shadow(0 0 18px rgba(212,162,84,0.3))" }}
              />
            </div>

            <div className="cg-divider my-6" />

            <div className="flex items-center justify-center gap-2 mb-4">
              <ShieldCheck size={16} style={{ color: "#d4a254" }} />
              <p className="colox-sans" style={{ color: "#c9b896", fontSize: "0.72rem", letterSpacing: "0.22em", textTransform: "uppercase" }}>
                Creation Provenance
              </p>
            </div>
            <p className="colox-serif mb-6 max-w-xl mx-auto" style={{ color: "rgba(201,184,150,0.8)", fontSize: "1.02rem", lineHeight: 1.7 }}>
              In the spirit of transparency, we acknowledge how this work was made — human vision guiding every stage,
              with tools used in service of the art, not in place of it.
            </p>
            <div className="grid gap-3 sm:grid-cols-3 text-left" data-testid="provenance-grid">
              {[
                { k: "Concept & Direction", v: "Human" },
                { k: "Creation", v: "AI-Assisted" },
                { k: "Refinement", v: "Human-Enhanced" },
              ].map((p) => (
                <div key={p.k} className="rounded-lg px-4 py-3" style={{ background: "rgba(212,162,84,0.06)", border: "1px solid rgba(212,162,84,0.18)" }}>
                  <p className="colox-sans" style={{ color: "rgba(201,184,150,0.65)", fontSize: "0.64rem", letterSpacing: "0.1em", textTransform: "uppercase" }}>{p.k}</p>
                  <p className="colox-serif" style={{ color: "#e8c87a", fontSize: "1.15rem" }}>{p.v}</p>
                </div>
              ))}
            </div>
            <p className="colox-sans mt-6" style={{ color: "rgba(201,184,150,0.72)", fontSize: "0.8rem", lineHeight: 1.7 }}>
              © {new Date().getFullYear()} INSPIYAH · World of INSPIYAH. The CreatorEcho mark denotes a work shaped through our
              creation process. All rights reserved.
            </p>
          </Reveal>
        </section>
      </div>
    </div>
  );
};

export default ColoringExperienceGuide;
